import dayjs from "dayjs";
import { formatPhoneNumber } from "react-phone-number-input";
import plotLogo from "../../../assets/png/logo-black.png";
import { GlobalStoreContextType } from "../../../context/GlobalStoreContext";
import Helper from "../../../Helper";
import { CalendarEvent } from "../../../models/CalendarEvent";
import { Delivery } from "../../../models/Delivery";
import { GateToDropoffRoute } from "../../../models/GateToDropoffRoute";
import { HaulRoute } from "../../../models/HaulRoute";
import { MapObject } from "../../../models/MapObject";
import TeamInfo from "../../../teamInfo";
import UserInfo from "../../../userInfo";
import { embeddedTeamLogo } from "../../PDF/Shared/pdfFiles";
import { pdfMapHasGeotiff } from "../../PDF/Shared/PdfMapView";
import { DamnMapItems } from "../Map/useDamnMapItems";
import { DamnPdfData, DamnPdfRoute, DamnPdfTeam, DamnPdfVehicleSize } from "./damnPdfDocument";
import { damnPdfMapContents } from "./damnPdfMaps";

export interface DamnPdfSources {
    context: GlobalStoreContextType;
    delivery: Delivery;
    calendarEvent: CalendarEvent;
    mapItems: DamnMapItems;
    gcTeam: TeamInfo | null;
    receivingTeam: TeamInfo | null;
}

const VEHICLE_SIZES: DamnPdfVehicleSize[] = ["light", "medium", "heavy"];

const toTeam = async (team: TeamInfo | null): Promise<DamnPdfTeam | null> =>
    team ? { name: team.team_name, logo: await embeddedTeamLogo(team) } : null;

const routeSteps = (route: HaulRoute | GateToDropoffRoute): string[] =>
    (route.waypointDirection?.waypointSteps || [])
        .filter((step) => !step.hidden && !step._destroy && step.instruction)
        .sort((a, b) => a.stepNumber - b.stepNumber)
        .map((step) => step.instruction as string);

const toRoute = (
    route: HaulRoute | GateToDropoffRoute,
    fallbackName: string,
    mapObject: MapObject | null
): DamnPdfRoute => ({
    name: route.name || fallbackName,
    steps: routeSteps(route),
    mapObject,
});

const timeZoneAbbreviation = (date: Date, timeZone: string | undefined) =>
    new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName")?.value || "";

// The same link the delivery's Share section sends, on the project's team subdomain wherever this is exported from.
export const damnPdfLiveUrl = (project: any, delivery: Delivery) =>
    `${Helper.baseUrlAltSubdomain(project?.claimed_by_team_subdomain || "projects")}/event/${delivery.uniqueToken}`;

// Everything the manifest can show for this delivery. Which parts actually appear is decided by DamnPdfInclude.
export const buildDamnPdfData = async ({
    context,
    delivery,
    calendarEvent,
    mapItems,
    gcTeam,
    receivingTeam,
}: DamnPdfSources): Promise<DamnPdfData> => {
    const project = mapItems.project || context.currentProjectInfo;
    const mapContents = damnPdfMapContents(mapItems, context, true);
    const timeZone = project?.timezone || undefined;
    const start = calendarEvent.startAt;
    const end = calendarEvent.endAt;

    const equipment = context
        // The delivery's own project: from the team calendar it may not be the current one.
        .equipmentForProject(project.unique_token)
        .filter((eq) => calendarEvent.deliveryEquipmentIds().includes(eq.id) && !!eq.equipmentName)
        .map((eq) => ({ name: eq.equipmentName as string, mapObject: eq.getMapObject(context) }));

    // Matches DamnContactInformation: lead names and numbers are only shown to signed-in users.
    const contacts = context.userInfo
        ? (receivingTeam?.users || [])
              .filter((user: UserInfo) => delivery.receivingTeamLeads?.includes(user.id))
              .map((lead: UserInfo) => ({
                  name: lead.fullname,
                  phone: lead.mobiles?.length > 0 ? formatPhoneNumber(lead.mobiles[0].mobile) || null : null,
              }))
        : null;

    const vehicleSize = VEHICLE_SIZES.find((size) => size === delivery.vehicleType) || null;
    const vehicle = delivery.vehicleType
        ? {
              label: `${delivery.vehicleType.charAt(0).toUpperCase()}${delivery.vehicleType.slice(1)} vehicle${
                  delivery.vehicleQuantity > 1 ? ` (x${delivery.vehicleQuantity})` : ""
              }`,
              size: vehicleSize,
          }
        : null;

    const [generalContractor, receivingTeamData] = await Promise.all([toTeam(gcTeam), toTeam(receivingTeam)]);

    return {
        deliveryName: calendarEvent.name || "Delivery",
        projectName: project?.project_name || "",
        dateLabel: start.format("dddd, MMMM D, YYYY"),
        windowLabel: `${start.format("h:mm A")} - ${end.format("h:mm A")} ${timeZoneAbbreviation(
            start.toDate(),
            timeZone
        )}`.trim(),
        status: delivery.status || "",
        vehicle,
        instructions: calendarEvent.description?.trim() || null,
        // Blank entries are left over from rows added in the settings but never filled in.
        ppe: ((project?.ppe_required as string[] | undefined) || []).map((item) => item?.trim()).filter(Boolean),
        haulRoute: mapItems.selectedHaulRoute
            ? toRoute(mapItems.selectedHaulRoute, "Haul route", mapItems.selectedHaulRouteMapObject)
            : null,
        // Once a route is assigned it's the one to take, so the others are left off.
        alternateHaulRoutes: mapItems.selectedHaulRoute
            ? []
            : mapItems.haulRoutes.map((route) => ({
                  name: route.name || "Haul route",
                  mapObject: route.getMapObject(context),
              })),
        gate: mapItems.gate
            ? {
                  name: mapItems.gate.name || "Gate",
                  details: mapItems.gate.details || null,
                  mapObject: mapItems.gateMapObject,
              }
            : null,
        gateToDropoffRoutes: mapItems.gateToDropoffRoutes.map((route) =>
            toRoute(route, "Gate-to-dropoff route", route.getMapObject(context))
        ),
        dropoff: mapItems.dropoff
            ? {
                  name: mapItems.dropoff.name || "Dropoff",
                  details: mapItems.dropoff.details || null,
                  mapObject: mapItems.dropOffMapObject,
              }
            : null,
        equipment,
        generalContractor,
        receivingTeam: receivingTeamData,
        contacts,
        liveUrl: damnPdfLiveUrl(project, delivery),
        // Drawn separately (see PdfQrCode) and added by the export dialog once it's ready.
        qrCode: null,
        logoUrl: plotLogo,
        // Which maps there's something to show on; their images are drawn separately and added by the export dialog.
        maps: {
            approach: { available: mapContents.approach.fit.length > 0, image: null },
            site: { available: mapContents.site.fit.length > 0, image: null },
        },
        geotiff: pdfMapHasGeotiff(project),
        generatedLabel: `Generated ${dayjs().format("MMM D, YYYY h:mm A")}`,
    };
};
